import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

/**
 * CollaborativeActorsManager Component
 *
 * Manages collaborative actors and their assigned questions/sections/answers.
 * Allows users to:
 * - Add/remove actors
 * - Edit actor names
 * - Assign sections to actors (assigns all questions in that section)
 * - Assign individual questions to actors
 * - Assign specific answers within multiple-choice/PDCA questions to actors
 *
 * @param {Object} props
 * @param {Array} props.collaborativeFormsSchema - Array of {actor: {id, name}, questions: [questionIds], sections: [sectionIds], answers: {questionId: [answerIds]}}
 * @param {Function} props.onChange - Callback when schema changes
 * @param {Array} props.formSections - All sections with questions from the form
 * @param {Object} props.colors - Color scheme object
 */
const CollaborativeActorsManager = ({ collaborativeFormsSchema = [], onChange, formSections = [], colors }) => {
  const { t } = useTranslation('labels');

  const [selectedActorId, setSelectedActorId] = useState(null);
  const [editingActorId, setEditingActorId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState({}); // Track which questions are expanded to show answers

  // Initialize with first actor selected if available
  useEffect(() => {
    if (collaborativeFormsSchema.length > 0 && !selectedActorId) {
      setSelectedActorId(collaborativeFormsSchema[0].actor.id);
    }
  }, [collaborativeFormsSchema, selectedActorId]);

  /**
   * Generate next actor ID (auto-increment)
   */
  const getNextActorId = () => {
    if (collaborativeFormsSchema.length === 0) return 1;
    const maxId = Math.max(...collaborativeFormsSchema.map(item => item.actor.id));
    return maxId + 1;
  };

  /**
   * Add a new actor
   */
  const handleAddActor = () => {
    const newId = getNextActorId();
    const newActor = {
      actor: {
        id: newId,
        name: `${t('actor')} ${newId}`
      },
      questions: [],
      sections: [], // Add sections array
      answers: {} // Add answers object for specific answer assignments
    };

    const updatedSchema = [...collaborativeFormsSchema, newActor];
    onChange(updatedSchema);
    setSelectedActorId(newId);
  };

  /**
   * Remove an actor (only if not the last one)
   */
  const handleRemoveActor = (actorId) => {
    if (collaborativeFormsSchema.length <= 1) return;

    const updatedSchema = collaborativeFormsSchema.filter(item => item.actor.id !== actorId);
    onChange(updatedSchema);

    // Select another actor if the removed one was selected
    if (selectedActorId === actorId && updatedSchema.length > 0) {
      setSelectedActorId(updatedSchema[0].actor.id);
    }
  };

  /**
   * Start editing an actor name
   */
  const startEditingName = (actor) => {
    setEditingActorId(actor.id);
    setEditingName(actor.name);
  };

  /**
   * Save edited actor name
   */
  const saveActorName = () => {
    if (!editingName.trim()) return;

    const updatedSchema = collaborativeFormsSchema.map(item => {
      if (item.actor.id === editingActorId) {
        return {
          ...item,
          actor: {
            ...item.actor,
            name: editingName.trim()
          }
        };
      }
      return item;
    });

    onChange(updatedSchema);
    setEditingActorId(null);
    setEditingName('');
  };

  /**
   * Cancel editing actor name
   */
  const cancelEditingName = () => {
    setEditingActorId(null);
    setEditingName('');
  };

  /**
   * Toggle section assignment for selected actor
   * When a section is assigned, all its questions are automatically assigned
   */
  const toggleSectionAssignment = (sectionId) => {
    const section = formSections.find(s => s.id === sectionId);
    if (!section) return;

    const sectionQuestionIds = section.questions?.map(q => q.id) || [];

    const updatedSchema = collaborativeFormsSchema.map(item => {
      if (item.actor.id === selectedActorId) {
        const isSectionAssigned = (item.sections || []).includes(sectionId);

        if (isSectionAssigned) {
          // Remove section and its questions
          return {
            ...item,
            sections: (item.sections || []).filter(sId => sId !== sectionId),
            questions: item.questions.filter(qId => !sectionQuestionIds.includes(qId))
          };
        } else {
          // Add section and all its questions
          return {
            ...item,
            sections: [...(item.sections || []), sectionId],
            questions: [...new Set([...item.questions, ...sectionQuestionIds])] // Use Set to avoid duplicates
          };
        }
      }
      return item;
    });

    onChange(updatedSchema);
  };

  /**
   * Toggle question assignment for selected actor
   * If the question's section is assigned, unassign the section first
   */
  const toggleQuestionAssignment = (questionId) => {
    // Find which section this question belongs to
    const parentSection = formSections.find(section =>
      section.questions?.some(q => q.id === questionId)
    );

    const updatedSchema = collaborativeFormsSchema.map(item => {
      if (item.actor.id === selectedActorId) {
        const isAssigned = item.questions.includes(questionId);
        const isSectionAssigned = parentSection && (item.sections || []).includes(parentSection.id);

        let newSections = item.sections || [];

        // If toggling off a question and its section is assigned, remove the section assignment
        if (isAssigned && isSectionAssigned) {
          newSections = newSections.filter(sId => sId !== parentSection.id);
        }

        return {
          ...item,
          sections: newSections,
          questions: isAssigned
            ? item.questions.filter(qId => qId !== questionId)
            : [...item.questions, questionId]
        };
      }
      return item;
    });

    onChange(updatedSchema);
  };

  /**
   * Check if a section is assigned to the selected actor
   */
  const isSectionAssigned = (sectionId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
    return actor ? (actor.sections || []).includes(sectionId) : false;
  };

  /**
   * Check if a question is assigned to the selected actor
   */
  const isQuestionAssigned = (questionId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
    return actor ? actor.questions.includes(questionId) : false;
  };

  /**
   * Check if a specific answer is assigned to the selected actor
   */
  const isAnswerAssigned = (questionId, answerId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
    if (!actor || !actor.answers) return false;
    return actor.answers[questionId]?.includes(answerId) || false;
  };

  /**
   * Toggle answer assignment for selected actor (for multiple-choice/PDCA questions)
   */
  const toggleAnswerAssignment = (questionId, answerId) => {
    const updatedSchema = collaborativeFormsSchema.map(item => {
      if (item.actor.id === selectedActorId) {
        const answers = item.answers || {};
        const questionAnswers = answers[questionId] || [];
        const isAssigned = questionAnswers.includes(answerId);

        let newAnswers;
        if (isAssigned) {
          // Remove answer
          const filtered = questionAnswers.filter(aId => aId !== answerId);
          if (filtered.length === 0) {
            // Remove the question key if no answers left
            newAnswers = { ...answers };
            delete newAnswers[questionId];
          } else {
            newAnswers = { ...answers, [questionId]: filtered };
          }
        } else {
          // Add answer
          newAnswers = { ...answers, [questionId]: [...questionAnswers, answerId] };
        }

        // If assigning specific answers, ensure the question itself is assigned
        let newQuestions = item.questions || [];
        if (!isAssigned && !newQuestions.includes(questionId)) {
          newQuestions = [...newQuestions, questionId];
        }

        return {
          ...item,
          questions: newQuestions,
          answers: newAnswers
        };
      }
      return item;
    });

    // Debug log
    console.log('Updated schema after answer assignment:', updatedSchema);

    onChange(updatedSchema);
  };

  /**
   * Toggle all answers for a multiple-choice/PDCA question
   */
  const toggleAllAnswers = (question) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
    if (!actor) return;

    const allAnswerIds = question.answers?.filter(a => !a.invisible).map(a => a.answerId || a.id) || [];
    const currentAnswers = actor.answers?.[question.id] || [];
    const allAssigned = allAnswerIds.length > 0 && allAnswerIds.every(aId => currentAnswers.includes(aId));

    const updatedSchema = collaborativeFormsSchema.map(item => {
      if (item.actor.id === selectedActorId) {
        const answers = item.answers || {};
        let newAnswers;

        if (allAssigned) {
          // Remove all answers
          newAnswers = { ...answers };
          delete newAnswers[question.id];
        } else {
          // Assign all answers
          newAnswers = { ...answers, [question.id]: allAnswerIds };
        }

        // Ensure question is assigned
        let newQuestions = item.questions || [];
        if (!allAssigned && !newQuestions.includes(question.id)) {
          newQuestions = [...newQuestions, question.id];
        }

        return {
          ...item,
          questions: newQuestions,
          answers: newAnswers
        };
      }
      return item;
    });

    onChange(updatedSchema);
  };

  /**
   * Check if a question supports answer-level assignment (multiple or PDCA)
   */
  const supportsAnswerAssignment = (question) => {
    return question.questionType === 'multiple' || question.questionType === 'pdca';
  };

  /**
   * Toggle expansion of a question to show its answers
   */
  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  /**
   * Get assigned questions count for an actor (including questions from assigned sections)
   */
  const getAssignedQuestionsCount = (actorId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === actorId);
    return actor ? actor.questions.length : 0;
  };

  /**
   * Get assigned sections count for an actor
   */
  const getAssignedSectionsCount = (actorId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === actorId);
    return actor ? (actor.sections || []).length : 0;
  };

  /**
   * Check if all questions in a section are individually assigned
   * (used to show indeterminate state when some but not all questions are assigned)
   */
  const getSectionAssignmentState = (sectionId) => {
    const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
    if (!actor) return { assigned: false, indeterminate: false };

    const section = formSections.find(s => s.id === sectionId);
    if (!section || !section.questions || section.questions.length === 0) {
      return { assigned: false, indeterminate: false };
    }

    const sectionQuestionIds = section.questions.map(q => q.id);
    const assignedCount = sectionQuestionIds.filter(qId => actor.questions.includes(qId)).length;

    const isSectionFullyAssigned = (actor.sections || []).includes(sectionId);
    const isIndeterminate = !isSectionFullyAssigned && assignedCount > 0 && assignedCount < sectionQuestionIds.length;

    return {
      assigned: isSectionFullyAssigned || assignedCount === sectionQuestionIds.length,
      indeterminate: isIndeterminate
    };
  };

  /**
   * Get selected actor data
   */
  const getSelectedActor = () => {
    return collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
  };

  /**
   * Flatten all questions from all sections
   */
  const getAllQuestions = () => {
    const questions = [];
    formSections.forEach((section, sectionIndex) => {
      section.questions?.forEach((question, questionIndex) => {
        questions.push({
          ...question,
          sectionIndex,
          sectionName: section.sectionName || `${t('section')} ${sectionIndex + 1}`,
          displayText: question.questionText?.[0]?.text || question.questionCode || `${t('question')} ${questionIndex + 1}`
        });
      });
    });
    return questions;
  };

  const selectedActor = getSelectedActor();
  const allQuestions = getAllQuestions();

  return (
    <div className="collaborative-actors-manager mt-3">
      <div className="row">
        {/* Left Panel - Actors List */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.secondaryLight }}>
              <h6 className="mb-0">
                {t('actors')}
              </h6>
              <button
                className="btn btn-sm"
                style={{ backgroundColor: colors.white, color: colors.dark }}
                onClick={handleAddActor}
                title={t('addActor')}
              >
                <FaPlus className="me-1" /> {t('addActor')}
              </button>
            </div>
            <div className="card-body" style={{ minHeight: '400px', maxHeight: '500px', overflowY: 'auto' }}>
              {collaborativeFormsSchema.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <FaPlus style={{ fontSize: '3rem' }} className="mb-3" />
                  <p className="mb-1 fw-bold">{t('noActors')}</p>
                  <small>{t('noActorsDescription')}</small>
                </div>
              ) : (
                <div className="actors-list">
                  {collaborativeFormsSchema.map((item) => {
                    const isSelected = selectedActorId === item.actor.id;
                    const questionsCount = getAssignedQuestionsCount(item.actor.id);
                    const isEditing = editingActorId === item.actor.id;

                    return (
                      <div
                        key={item.actor.id}
                        className={`actor-item card mb-2 ${isSelected ? 'border-dark' : ''}`}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: isSelected ? colors.secondaryLight : 'white'
                        }}
                      >
                        <div className="card-body py-2 px-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div
                              className="flex-grow-1"
                              onClick={() => !isEditing && setSelectedActorId(item.actor.id)}
                            >
                              {isEditing ? (
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && saveActorName()}
                                    autoFocus
                                  />
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={saveActorName}
                                    title={t('save')}
                                  >
                                    <FaCheck />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={cancelEditingName}
                                    title={t('cancel')}
                                  >
                                    <FaTimes />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="fw-bold">{item.actor.name}</div>
                                  <small className="text-muted">
                                    {t('sections', { ns: 'labels' })}: <strong>{getAssignedSectionsCount(item.actor.id)}</strong>
                                    {' | '}
                                    {t('questions', { ns: 'labels' })}: <strong>{questionsCount}</strong>
                                  </small>
                                </>
                              )}
                            </div>
                            {!isEditing && (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditingName(item.actor);
                                  }}
                                  title={t('edit')}
                                >
                                  <FaEdit />
                                </button>
                                {collaborativeFormsSchema.length > 1 && (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveActor(item.actor.id);
                                    }}
                                    title={t('removeActor')}
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Questions Assignment */}
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-header" style={{ backgroundColor: colors.secondaryLight }}>
              {selectedActor ? (
                <h6 className="mb-0 fw-bold">
                  {t('assignQuestions')} - {selectedActor.actor.name}
                </h6>
              ) : (
                <h6 className="mb-0 fw-bold">
                  {t('assignQuestions')}
                </h6>
              )}
            </div>
            <div className="card-body" style={{ minHeight: '400px', maxHeight: '500px', overflowY: 'auto' }}>
              {selectedActor ? (
                <>
                  <div className="text-muted small mb-3 p-2 border-start border-3 border-secondary" style={{ backgroundColor: colors.light }}>
                    {t('selectSectionsAndQuestionsToAssign')}
                  </div>

                  {allQuestions.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <p>{t('noQuestions')}</p>
                    </div>
                  ) : (
                    <div className="questions-list">
                      {/* Group questions by section */}
                      {formSections.map((section, sectionIndex) => {
                        const sectionQuestions = allQuestions.filter(q => q.sectionIndex === sectionIndex);
                        if (sectionQuestions.length === 0) return null;

                        const sectionAssignmentState = getSectionAssignmentState(section.id);
                        const sectionName = section.name?.find(n => n.language === 'it')?.text ||
                                          section.name?.find(n => n.language === 'en')?.text ||
                                          `${t('section')} ${sectionIndex + 1}`;

                        return (
                          <div key={sectionIndex} className="mb-4">
                            {/* Section-level checkbox */}
                            <div
                              className="form-check mb-2 p-3 border rounded"
                              style={{
                                backgroundColor: sectionAssignmentState.assigned ? colors.primaryLight : colors.light,
                                borderColor: sectionAssignmentState.assigned ? colors.primary : colors.secondary,
                                borderWidth: '2px',
                                transition: 'all 0.2s'
                              }}
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`section-${section.id}`}
                                checked={sectionAssignmentState.assigned}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = sectionAssignmentState.indeterminate;
                                  }
                                }}
                                onChange={() => toggleSectionAssignment(section.id)}
                                style={{ transform: 'scale(1.3)' }}
                              />
                              <label
                                className="form-check-label flex-grow-1 ms-2 fw-bold"
                                htmlFor={`section-${section.id}`}
                                style={{ cursor: 'pointer', fontSize: '1.05rem' }}
                              >
                                <div className="d-flex align-items-center">
                                  <span className="badge me-2" style={{
                                    backgroundColor: colors.primary,
                                    color: colors.white
                                  }}>
                                    {section.sectionCode || `S${sectionIndex + 1}`}
                                  </span>
                                  <span>{sectionName}</span>
                                  <small className="ms-2 text-muted">
                                    ({sectionQuestions.length} {t('questions', { ns: 'labels' })})
                                  </small>
                                </div>
                              </label>
                            </div>

                            {/* Individual questions - with left margin for hierarchy */}
                            <div className="ms-4">
                            {sectionQuestions.map((question) => {
                              const isAssigned = isQuestionAssigned(question.id);
                              // Extract text from EditorJS format if needed
                              let displayText = question.questionCode || '';
                              try {
                                const parsed = JSON.parse(question.displayText);
                                if (parsed.blocks && parsed.blocks.length > 0) {
                                  displayText = parsed.blocks[0].data.text || question.questionCode;
                                  // Remove HTML tags for display
                                  displayText = displayText.replace(/<[^>]*>/g, '');
                                }
                              } catch {
                                displayText = question.displayText || question.questionCode;
                              }

                              const hasAnswerAssignment = supportsAnswerAssignment(question);
                              const isExpanded = expandedQuestions[question.id];
                              const actor = collaborativeFormsSchema.find(item => item.actor.id === selectedActorId);
                              const assignedAnswersCount = actor?.answers?.[question.id]?.length || 0;

                              return (
                                <div key={question.id} className="mb-2">
                                  <div
                                    className="form-check p-2 border rounded"
                                    style={{
                                      backgroundColor: isAssigned ? colors.light : 'white',
                                      borderColor: isAssigned ? colors.secondary : colors.secondaryLight,
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <div className="d-flex align-items-start">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`question-${question.id}`}
                                        checked={isAssigned}
                                        onChange={() => toggleQuestionAssignment(question.id)}
                                      />
                                      <label
                                        className="form-check-label flex-grow-1 ms-2"
                                        htmlFor={`question-${question.id}`}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <div className="d-flex align-items-start justify-content-between">
                                          <div className="d-flex align-items-start flex-grow-1">
                                            <span className="badge bg-secondary me-2">{question.questionCode}</span>
                                            <span dangerouslySetInnerHTML={{ __html: displayText }} />
                                            {hasAnswerAssignment && (
                                              <span className="badge bg-info ms-2" style={{ fontSize: '0.7rem' }}>
                                                {question.questionType === 'pdca' ? 'PDCA' : t('multipleChoice', { ns: 'labels' })}
                                              </span>
                                            )}
                                          </div>
                                          {hasAnswerAssignment && (
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                toggleQuestionExpansion(question.id);
                                              }}
                                              style={{
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              {isExpanded ? t('hideAnswers', { ns: 'labels' }) : t('viewAnswers', { ns: 'labels' })}
                                              {assignedAnswersCount > 0 && ` (${assignedAnswersCount})`}
                                            </button>
                                          )}
                                        </div>
                                      </label>
                                    </div>
                                  </div>

                                  {/* Answers sub-section for multiple/PDCA questions */}
                                  {hasAnswerAssignment && isExpanded && (
                                    <div className="ms-5 mt-2 p-2 border-start" style={{ borderColor: colors.secondary }}>
                                      <div className="mb-2">
                                        <button
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() => toggleAllAnswers(question)}
                                        >
                                          {t('selectAllAnswers', { ns: 'labels' })}
                                        </button>
                                      </div>
                                      {question.answers?.filter(a => !a.invisible).map((answer) => {
                                        const answerAssigned = isAnswerAssigned(question.id, answer.answerId || answer.id);
                                        let answerText = '';
                                        try {
                                          if (typeof answer.answerText === 'string') {
                                            answerText = answer.answerText;
                                          } else if (Array.isArray(answer.answerText)) {
                                            const textObj = answer.answerText.find(t => t.language === 'it') || answer.answerText[0];
                                            answerText = textObj?.text || answer.answerCode || '';
                                          }
                                        } catch {
                                          answerText = answer.answerCode || '';
                                        }

                                        return (
                                          <div
                                            key={answer.answerId || answer.id}
                                            className="form-check mb-1 p-1 rounded"
                                            style={{
                                              backgroundColor: answerAssigned ? colors.secondaryLight : 'white',
                                              transition: 'all 0.2s'
                                            }}
                                          >
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              id={`answer-${question.id}-${answer.answerId || answer.id}`}
                                              checked={answerAssigned}
                                              onChange={() => toggleAnswerAssignment(question.id, answer.answerId || answer.id)}
                                            />
                                            <label
                                              className="form-check-label ms-2"
                                              htmlFor={`answer-${question.id}-${answer.answerId || answer.id}`}
                                              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                            >
                                              <span className="badge bg-light text-dark me-1" style={{ fontSize: '0.7rem' }}>
                                                {answer.answerCode}
                                              </span>
                                              {answerText}
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <p>{t('selectActor')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Warning */}
      {collaborativeFormsSchema.some(item => item.questions.length === 0 && (item.sections || []).length === 0) && (
        <div className="alert alert-warning mt-3 mb-0">
          <FaExclamationTriangle className="me-2" />
          <small>{t('actorMustHaveQuestionsOrSections')}</small>
        </div>
      )}
    </div>
  );
};

export default CollaborativeActorsManager;
